import { ReactNode } from 'react';
import Const from 'src/config/Const';
import Head from 'next/head';

export type Metadata = {
  title: string;
  author: string;
  orgAuthor: string;
  description: string;
  date: string;
  category: string;
  cover: string;
  url: string;
  tags: string;
  faviconUrl?: string;
  avoidSeoIndex?: boolean;
};

type CommonHeadProps = {
  metadata: Metadata;
  children?: ReactNode;
};

const CommonHead = (props: CommonHeadProps): JSX.Element => {
  const { metadata, children } = props;

  const faviconUrl =
    metadata.faviconUrl || `${Const.cdnUrl}/design-system/images/favicons`;

  const title = metadata.title
    ? `${metadata.title}${metadata.title.includes('Beɘk') ? '' : ' | Beek'}`
    : `Beɘk | Audiolibros y meditaciones sin límites`;

  return (
    <Head>
      <meta charSet="utf-8" key="charSet" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" key="httpEquiv" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1"
        key="viewport"
      />
      <title>{title}</title>
      <meta
        name="description"
        content={metadata.description.replace(/(<([^>]+)>)/gi, '')}
        key="description"
      />
      <meta name="author" content={metadata.orgAuthor} key="author" />
      <meta name="copyright" content="Beek Technologies" />
      <meta name="rating" content="general" />
      {metadata.avoidSeoIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      <meta
        name="msapplication-TileImage"
        content={`${faviconUrl}/ms-icon-70x70.png`}
      />
      <meta name="msapplication-TileColor" content="#00CCFF" />

      <link rel="shortcut icon" href={`${faviconUrl}/favicon.ico`} />
      <link
        rel="icon"
        type="image/png"
        href={`${faviconUrl}/faviconBeek-57.png`}
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="144x144"
        href={`${faviconUrl}/apple-icon-144x144.png`}
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="114x114"
        href={`${faviconUrl}/apple-icon-114x114.png`}
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="72x72"
        href={`${faviconUrl}/apple-icon-72x72.png`}
      />
      <link
        rel="apple-touch-icon-precomposed"
        href={`${faviconUrl}/apple-icon-57x57.png`}
      />

      <link rel="preconnect" href="https://cdn.beek.io" />
      <link rel="dns-prefetch" href="https://cdn.beek.io" />
      <link rel="preconnect" href="https://nicephore.beek.io" />
      <link rel="dns-prefetch" href="https://nicephore.beek.io" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metadata.title} />
      <meta name="twitter:site" content="@beek_io" />
      <meta
        name="twitter:description"
        content={metadata.description.replace(/(<([^>]+)>)/gi, '')}
      />
      <meta name="twitter:image" content={metadata.cover} />
      <meta name="twitter:image:alt" content={metadata.cover} />

      <meta property="og:type" content="article" />
      <meta property="og:title" content={metadata.title} />
      <meta property="og:url" content={metadata.url} />
      <meta property="og:image" content={metadata.cover} />
      <meta
        property="og:description"
        content={metadata.description.replace(/(<([^>]+)>)/gi, '')}
      />
      <meta property="article:author" content={metadata.orgAuthor} />
      <meta property="article:published_time" content={metadata.date} />
      <meta property="article:section" content={metadata.category} />
      <meta property="article:tag" content={metadata.tags} />

      <script
        type="application/ld+json"
        // eslint-disable-next-line
        dangerouslySetInnerHTML={{
          __html: `
              {
                "@context": "http://schema.org/",
                "@type": "Article",
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": "https://beek.io"
                },
                "author": {
                  "@type": "Organization",
                  "name": "${metadata.orgAuthor}"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "${metadata.orgAuthor}",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "${faviconUrl}/apple-icon-144x144.png"
                  }
                },
                "headline": "${metadata.title}",
                "image": "${metadata.cover}",
                "datePublished": "${metadata.date}",
                "dateModified": "${metadata.date}"
              }
            `,
        }}
      />
      {children}
    </Head>
  );
};

CommonHead.defaultProps = {
  children: null,
};

export default CommonHead;
